import { motion } from "framer-motion";
import { Check, Phone, Mail } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { QuoteForm } from "../components/QuoteForm";
import { BRAND } from "../lib/site-data";

const PROMISES = [
  "Response within 1 business day",
  "Clear, itemized pricing — no surprises",
  "Free in-home measurements",
  "Licensed & insured craftsmen",
];

export default function QuotePage() {
  return (
    <main data-testid="quote-page">
      <PageHeader
        eyebrow="Tell us about it"
        title="Get a quote."
        accent="It's easy."
        subtitle="Share a few details about your project and we'll come back with a clear estimate within one business day."
      />

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Left — promises */}
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5 lg:sticky lg:top-28 h-fit space-y-8"
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-8 bg-[#D4AF37]" />
                  <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                    What to expect
                  </span>
                </div>
                <ul className="space-y-4">
                  {PROMISES.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                        <Check
                          size={11}
                          strokeWidth={2.5}
                          className="text-[#D4AF37]"
                        />
                      </span>
                      <span className="text-[15px] text-slate-200 leading-relaxed">
                        {p}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-8 border-t border-white/10">
                <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mb-4">
                  Rather speak directly?
                </div>
                <div className="space-y-3">
                  <a
                    href={`tel:${BRAND.phones[0].tel}`}
                    className="flex items-center gap-3 group"
                  >
                    <Phone
                      size={14}
                      strokeWidth={1.5}
                      className="text-[#D4AF37]"
                    />
                    <span className="text-[15px] text-white group-hover:text-[#D4AF37] transition-colors">
                      {BRAND.phones[0].value}
                    </span>
                  </a>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="flex items-center gap-3 group"
                  >
                    <Mail
                      size={14}
                      strokeWidth={1.5}
                      className="text-[#D4AF37]"
                    />
                    <span className="text-[15px] text-white group-hover:text-[#D4AF37] transition-colors break-all">
                      {BRAND.email}
                    </span>
                  </a>
                </div>
              </div>
            </motion.aside>

            {/* Right — form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7"
            >
              <QuoteForm />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
